import React, { useEffect, useRef, useState } from 'react'
import { IconButton, Button, Typography, Tooltip, List, ListItem, Grid } from '@mui/material'
import { apiGet, apiPost, apiDelete } from '../../generic/Api_Functions'
import { Edit, DeleteOutlineRounded, TextSnippetOutlined } from '@mui/icons-material'
import SmartMateriaUITable from '../../generic/SmartMateriaUITable'
import { useNavigate } from 'react-router-dom'
import { useLocation } from 'react-router'
import AlertUI from '../../generic/AlertUI'
import SmartDialog from './../../generic/utilities/SmartDialog/SmartDialog'
import createDialogTitle from '../../generic/utilities/dialogUtil'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { enumFilterOptions, systemValueLabel } from '../../generic/systemValues/systemValueLabel'
import DeviceSetResource from './DeviceSetResource'
import { useAuthorization } from '../../generic/hooks/useAuthorization'
import MoveDownIcon from '@mui/icons-material/MoveDown'
import DashboardCustomizeIcon from '@mui/icons-material/DashboardCustomize'
import Document from '../document/Document'
import ApplyWidgetTemplateDialog from '../../Mos/widgetTemplates/ApplyWidgetTemplateDialog'
import useRepositorySelection from '../../generic/hooks/useRepositorySelection'
import { parentPreselectedRecordFrom } from '../../generic/utilities/repositorySelectionMapping'

const Devices = (props) => {
  const navigate = useNavigate()
  const state = useLocation()
  // Historia 170 (J4b, S89): DEVICE has no child window (03-dominio.md's mechanism only preselects
  // this table itself). `useRepositorySelection` returns `null` for every OTHER caller (S106) —
  // `parentPreselectedRecordFrom(null)` is `null` too, inert.
  const repositorySelection = useRepositorySelection()
  const { unChecked } = props
  const [AlertElement, showAlert] = AlertUI()
  const [resetMultipleSelectionValue, setResetMultipleSelectionValue] = useState(false)
  const show = useRef(showAlert)
  const [showDialog, setShowDialog] = useState(false)
  const [workflowsList, setWorkflowsList] = useState(null)
  const [deviceList, setDeviceList] = useState(null)
  const [alertElementDeleteError, showAlertDeleteError] = AlertUI()
  const { t_ } = useSmartTranslation()
  const [dialogResourceSetOpen, setDialogResourceSetOpen] = useState(false)
  const [resourceSetDeviceName, setResourceSetDeviceName] = useState(null)
  const isUserAuthorized = useAuthorization()
  const [showDocument, setShowDocument] = useState(false)
  const [deviceRow, setDeviceRow] = useState(null)
  const [editing, setEditing] = useState(false)
  const [isDownloadingFile, setIsDownloadingFile] = useState(false)
  const [forceValuesChange, setForceValuesChange] = useState(null)
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false)
  const [templateDevice, setTemplateDevice] = useState(null)

  useEffect(() => {
    if (state.state === 'BACK') {
      show.current({
        severity: 'info'

      })
    } else if (state.state === 'EDIT') {
      show.current({
        title: 'Created correctly.',
        severity: 'success'
      })
    }
  }, [state])

  const actions = {
    custom: [
      {
        name: 'Documents',
        render: (row) => {
          return (
            <IconButton>
              <TextSnippetOutlined />
            </IconButton>
          )
        },
        disabled: (row) => (editing || !isUserAuthorized('DOCUMENTS_DEVICE')),
        onClick: (row) => {
          setDeviceRow(row)
          setShowDocument(true)
        }
      },
      {
        name: 'Update resource in asset',
        render: (row) => (
          <IconButton>
            <MoveDownIcon />
          </IconButton>
        ),
        onClick: async (row) => {
          if (row.deviceType.name === 'STORAGE') { // system-value: raw-ok
            const storageId = (await apiGet('storages/byDeviceId/' + row.id)).id
            navigate('/process/storage', { state: { initialStorageId: storageId } })
            return
          }
          setDialogResourceSetOpen(true)
          setResourceSetDeviceName(row.name)
        },
        disabled: !isUserAuthorized('ASSET_RESOURCE_MOVE')
      },
      {
        name: 'Change widget template',
        render: (row) => (
          <IconButton>
            <DashboardCustomizeIcon />
          </IconButton>
        ),
        onClick: (row) => {
          setTemplateDevice(row)
          setTemplateDialogOpen(true)
        },
        disabled: !isUserAuthorized('MOS_EDIT')
      },
      {
        name: 'Edit',
        render: (row) => (
          <IconButton>
            <Edit />
          </IconButton>
        ),
        onClick: (row) => {
          navigate('/configuration/assets/' + row.id)
        },
        disabled: !isUserAuthorized('PROPERTIES_DEVICE')
      },
      {
        name: 'Delete',
        render: (row) => (
          <IconButton>
            <DeleteOutlineRounded />
          </IconButton>
        ),
        onClick: (row) => {
          dialogAction('', '', row)
        },
        disabled: !isUserAuthorized('DELETE_DEVICE')
      }
    ],
    create: {
      fetchFunction: (newRow) => {
        return apiPost('devices', formatDeviceToPost(newRow))
      },
      disabled: !isUserAuthorized('ADD_DEVICE')
    }
  }

  // Status is derived from the selected mode and kept read-only: OFFLINE maps to
  // IDLE, any other mode maps to OFF.
  const onModeChange = (row) => {
    const status = row.mode === 'OFFLINE' ? 'IDLE' : 'OFF'
    if (row.status === status) {
      return
    }
    setForceValuesChange({
      rowsToChange: [{
        'smart-table-index': row['smart-table-index'],
        valuesToChange: [{ field: 'status', value: status }] // system-value: raw-ok
      }]
    })
  }

  const formatDeviceToPost = (device) => {
    const dev = { ...device }
    dev.toolChangeEnabled = false
    dev.deviceType = {
      id: device.deviceType.id,
      name: device.deviceType.name // system-value: raw-ok
    }
    return dev
  }

  const columns = [
    {
      name: t_('Name'),
      field: 'name',
      defaultNewValue: '',
      mandatory: true
    },
    {
      name: t_('Type'),
      field: 'deviceType.name',
      value: 'deviceType.id',
      mandatory: true,
      type: 'select',
      // Story 204 — S76 (DL17): the device type is an enum shown, labelled and filtered by its code; the value is its name.
      render: (row) => systemValueLabel(t_, 'DeviceTypeEnum', row.deviceType?.name || ''),
      renderOnlyInReadMode: true,
      filterByLabel: { options: (items) => items.map((deviceType) => ({ value: deviceType.name, label: systemValueLabel(t_, 'DeviceTypeEnum', deviceType.name) })) },
      edit: {
        source: async () => {
          const deviceTypes = await apiGet('deviceTypes')
          return deviceTypes
        },
        id: 'id',
        field: 'name',
        optionLabel: (name) => systemValueLabel(t_, 'DeviceTypeEnum', name)
      }
    },
    {
      name: t_('Mode'),
      field: 'mode',
      mandatory: true,
      type: 'select-plain',
      onChange: onModeChange,
      render: (row) => systemValueLabel(t_, 'DeviceModeEnum', row.mode || ''),
      renderOnlyInReadMode: true,
      edit: {
        source: async () => {
          const mode = await apiGet('devices/mode')
          return mode.filter((st) => st !== 'NON_OPERATIVE')
        },
        id: 'id',
        field: 'mode',
        optionLabel: (mode) => systemValueLabel(t_, 'DeviceModeEnum', mode || '')
      }
    },
    {
      name: t_('Status'),
      field: 'status',
      readOnly: () => true,
      render: (row) => systemValueLabel(t_, 'DeviceStatusEnum', row.status),
      filterByLabel: { options: enumFilterOptions(t_, 'DeviceStatusEnum') }
    },
    {
      name: t_('Description'),
      field: 'description',
      defaultNewValue: ''
    },
    // RN45 (whitelist, point 3): the asset configuration list is one of the three places where an
    // external asset is visible, so the mark has to be readable in the row. `defaultNewValue: false`
    // keeps a brand new asset internal (RN1) and makes the field travel in the POST body instead of
    // reaching Sun as an empty string.
    {
      name: t_('External'),
      field: 'external',
      type: 'boolean',
      defaultNewValue: false
    }
  ]

  const handleDelete = async (rows) => {
    try {
      if (rows.length === 1) {
        await apiDelete('device/' + rows[0].id)
      } else {
        await apiDelete('devicesMulti', rows)
      }
    } catch (error) {
      showAlertDeleteError({
        title: error.message,
        severity: 'error'
      })
    }
  }

  const dialogAction = async (dataFetch, title, row) => {
    let list = null
    let idListObject = null
    if (row !== undefined) {
      idListObject = { idList: row.id }
      dataFetch = [row]
    } else {
      idListObject = { idList: dataFetch.map(data => data.id) }
    }
    list = await apiGet('deviceTechnology/orphanWorkflowsIfDeviceDeleted', idListObject)

    setWorkflowsList(list)
    setDeviceList(dataFetch)
    setShowDialog(true)
  }

  const WorkflowsListDialog = (props) => {
    const { list } = props
    if (list !== null && list.length !== 0) {
      return (
        <Grid container alignItems='center' justifyContent='center'>
          <Grid item xs={12} sx={{ textAlign: 'center' }}>
            <Typography color='error'>{t_('The following workflow(s) have at least one technology configured that wont be available after delete:')}</Typography>
          </Grid>
          <Grid>
            <List sx={{
              listStyleType: 'disc',
              pl: 2,
              '& .MuiListItem-root': {
                display: 'list-item'
              }
            }}
            >
              {list.map((item, index) => (
                <ListItem key={index}> {item.name}</ListItem>
              ))}
            </List>
          </Grid>
        </Grid>
      )
    }
  }

  const multipleSelectionActions = [
    {
      field: 'checked',
      render: (datafetch, index) => {
        return (
          isUserAuthorized('DELETE_DEVICE')
            ? (
              <Tooltip arrow title='delete selections'>
                <Button
                  variant='outlined'
                  sx={{ border: '0px' }}
                  color='error'
                  key={'key' + index}
                  onClick={() => { dialogAction(datafetch, '') }}
                >
                  <DeleteOutlineRounded sx={{ marginRight: '0.5em' }} color='error' />
                  <Typography color='error'>{t_('DELETE')}</Typography>
                </Button>
              </Tooltip>)
            : null
        )
      }
    }
  ]

  return (
    <>
      <SmartDialog
        closeCallback={() => setShowDocument(false)}
        accept={false}
        cancel={false}
        close
        disableButtons={isDownloadingFile}
        renderComponent={<Document reference={deviceRow} resourceType='DEVICE' isDownloadingFile={isDownloadingFile} setIsDownloadingFile={setIsDownloadingFile} />}
        setOpen={showDocument}
      />
      {AlertElement}
      {alertElementDeleteError}
      <SmartDialog
        message={t_("Are you sure you want to delete the asset(s)? You can't undo this action.")}
        title={createDialogTitle({ color: 'error', title: t_('Delete data?'), icon: DeleteOutlineRounded })}
        accept={false}
        renderCustomButtons={
          <Button
            color='error' onClick={() => {
              handleDelete(deviceList)
              setResetMultipleSelectionValue(!resetMultipleSelectionValue)
              setShowDialog(false)
            }} variant='outlined'
          >
            <Typography color='error'> {t_('DELETE')} </Typography>
            <DeleteOutlineRounded color='error' sx={{ fontSize: '1.4em', marginLeft: '0.3rem' }} />
          </Button>
}
        cancelCallback={() => setShowDialog(false)}
        setOpen={showDialog}
        renderComponent={
          <>
            <WorkflowsListDialog list={workflowsList} />
            <SmartMateriaUITable
              columns={columns}
              dataFetch={deviceList}
            />
          </>
}
      />
      <SmartMateriaUITable
        tableTuneUniqueKey='device'
        title={t_('Assets')}
        columns={columns}
        dataFetch='deviceConfiguration'
        actions={actions}
        dense
        preselectedRecord={parentPreselectedRecordFrom(repositorySelection)}
        multipleSelection={!unChecked}
        multipleSelectionActions={multipleSelectionActions}
        deleteMultipleSelectionDisabled
        resetMultipleSelectionValue={resetMultipleSelectionValue}
        editingRow={(event) => setEditing(event)}
        forceValuesChange={forceValuesChange}
      />
      <DeviceSetResource
        open={dialogResourceSetOpen}
        deviceName={resourceSetDeviceName}
        setDeviceName={setResourceSetDeviceName}
        setOpen={setDialogResourceSetOpen}
      />
      <ApplyWidgetTemplateDialog
        open={templateDialogOpen}
        setOpen={setTemplateDialogOpen}
        device={templateDevice}
        onApplied={() => showAlert({ title: t_('Template applied'), severity: 'success' })}
      />
    </>
  )
}

export default Devices
